import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader, StatusBadge } from "@/components/common/Primitives";
import { DataTable } from "@/components/common/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtEUR, fmtDate } from "@/lib/format";
import { MF_VERSION_STATUS_MAP } from "@/lib/constants";
import { Plus, Layers, Send, Check, X, GitBranch } from "lucide-react";

export default function MfTariffs() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const write = can(user?.role, "mobilfunk:write");
  const [q, setQ] = useState("");
  const [create, setCreate] = useState(false);
  const [detail, setDetail] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ["mf-tariffs", q], queryFn: async () => (await api.get(`/mobilfunk/tariffs?q=${encodeURIComponent(q)}`)).data });

  const columns = [
    { key: "name", header: "Tarif", render: (t) => (
      <div className="min-w-0"><div className="flex items-center gap-2 font-medium">{t.name}{t.is_demo && <Badge variant="outline" className="text-[10px]">DEMO</Badge>}</div>
        <div className="text-xs text-muted-foreground">{t.provider_name || "—"} · {t.tariff_type} · ab {t.valid_from ? fmtDate(t.valid_from) : "—"}</div></div>
    ) },
    { key: "fee", header: "Grundgebühr", render: (t) => fmtEUR(t.monthly_fee) },
    { key: "versions", header: "Versionen", mobileHidden: true, render: (t) => <Badge variant="secondary">{t.version_count}</Badge> },
    { key: "status", header: "Status", render: (t) => <StatusBadge status={t.current_status} map={MF_VERSION_STATUS_MAP} /> },
  ];

  return (
    <div data-testid="mf-tariffs">
      <PageHeader title="Tarife" description={`${data?.total ?? 0} Tarife`}
        actions={write && <Button className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setCreate(true)} data-testid="new-tariff-button"><Plus className="h-4 w-4" />Neuer Tarif</Button>} />
      <div className="mb-4"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen…" className="sm:max-w-sm" data-testid="tariff-search" /></div>
      <DataTable columns={columns} rows={data?.items} loading={isLoading} testIdPrefix="tariff-row" onRowClick={(t) => setDetail(t.id)} />
      <CreateTariffDialog open={create} onClose={() => setCreate(false)} onDone={(id) => { setCreate(false); qc.invalidateQueries({ queryKey: ["mf-tariffs"] }); setDetail(id); }} />
      <TariffDrawer tid={detail} onClose={() => setDetail(null)} write={write} canApprove={can(user?.role, "mobilfunk:approve")} onChanged={() => qc.invalidateQueries({ queryKey: ["mf-tariffs"] })} />
    </div>
  );
}

const VEMPTY = { valid_from: "", valid_to: "", data_volume: "", voice: "", sms: "", monthly_fee: 0, onetime_fee: 0, setup_fee: 0, min_term_months: 24, change_summary: "" };

function CreateTariffDialog({ open, onClose, onDone }) {
  const [h, setH] = useState({ name: "", external_id: "", internal_id: "", tariff_type: "privat", channel: "", provider_id: "", distributor_id: "" });
  const [v, setV] = useState(VEMPTY);
  const { data: provs } = useQuery({ queryKey: ["mf-prov-min2"], queryFn: async () => (await api.get("/mobilfunk/providers")).data, enabled: open });
  const anbieter = (provs?.items || []).filter((p) => p.kind === "anbieter");
  const distis = (provs?.items || []).filter((p) => p.kind === "distributor");

  const submit = async (e) => {
    e.preventDefault();
    if (!h.name.trim()) return toast.error("Name erforderlich");
    if (!v.valid_from) return toast.error("Gültig-ab-Datum erforderlich");
    try {
      const { data } = await api.post("/mobilfunk/tariffs", h);
      await api.post(`/mobilfunk/tariffs/${data.id}/versions`, { ...v, monthly_fee: Number(v.monthly_fee) || 0, onetime_fee: Number(v.onetime_fee) || 0, setup_fee: Number(v.setup_fee) || 0, min_term_months: Number(v.min_term_months) || 0 });
      toast.success("Tarif angelegt (Entwurf v1)"); setH({ name: "", external_id: "", internal_id: "", tariff_type: "privat", channel: "", provider_id: "", distributor_id: "" }); setV(VEMPTY);
      onDone(data.id);
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="create-tariff-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Layers className="h-4 w-4" />Neuer Tarif</DialogTitle>
          <DialogDescription>Kopfdaten + erste Version (Entwurf). Freigabe erfolgt anschließend.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label>Tarifname</Label><Input value={h.name} onChange={(e) => setH((s) => ({ ...s, name: e.target.value }))} data-testid="tariff-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Kundentyp</Label>
              <Select value={h.tariff_type} onValueChange={(val) => setH((s) => ({ ...s, tariff_type: val }))}>
                <SelectTrigger data-testid="tariff-type-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="privat">Privat</SelectItem><SelectItem value="geschaeft">Geschäft</SelectItem></SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Externe ID</Label><Input value={h.external_id} onChange={(e) => setH((s) => ({ ...s, external_id: e.target.value }))} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Anbieter</Label>
              <Select value={h.provider_id || "none"} onValueChange={(val) => setH((s) => ({ ...s, provider_id: val === "none" ? "" : val }))}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{anbieter.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Distributor</Label>
              <Select value={h.distributor_id || "none"} onValueChange={(val) => setH((s) => ({ ...s, distributor_id: val === "none" ? "" : val }))}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{distis.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="rounded-md border border-border p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Erste Version</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Gültig ab</Label><Input type="date" value={v.valid_from} onChange={(e) => setV((s) => ({ ...s, valid_from: e.target.value }))} data-testid="tariff-validfrom" /></div>
              <div className="space-y-1.5"><Label>Grundgebühr €</Label><Input type="number" step="0.01" value={v.monthly_fee} onChange={(e) => setV((s) => ({ ...s, monthly_fee: e.target.value }))} data-testid="tariff-monthlyfee" /></div>
              <div className="space-y-1.5"><Label>Datenvolumen</Label><Input value={v.data_volume} onChange={(e) => setV((s) => ({ ...s, data_volume: e.target.value }))} placeholder="z. B. 20 GB" /></div>
              <div className="space-y-1.5"><Label>Laufzeit (Monate)</Label><Input type="number" value={v.min_term_months} onChange={(e) => setV((s) => ({ ...s, min_term_months: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Anschlussgebühr €</Label><Input type="number" step="0.01" value={v.setup_fee} onChange={(e) => setV((s) => ({ ...s, setup_fee: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Einmalgebühr €</Label><Input type="number" step="0.01" value={v.onetime_fee} onChange={(e) => setV((s) => ({ ...s, onetime_fee: e.target.value }))} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="tariff-create-save">Anlegen</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TariffDrawer({ tid, onClose, write, canApprove, onChanged }) {
  const qc = useQueryClient();
  const [addVer, setAddVer] = useState(false);
  const { data } = useQuery({ queryKey: ["mf-tariff", tid], queryFn: async () => (await api.get(`/mobilfunk/tariffs/${tid}`)).data, enabled: !!tid });
  const t = data?.tariff;
  const versions = data?.versions || [];

  const refresh = () => { qc.invalidateQueries({ queryKey: ["mf-tariff", tid] }); onChanged(); };
  const action = async (vid, act) => {
    try { await api.post(`/mobilfunk/tariff-versions/${vid}/${act}`); toast.success("Aktualisiert"); refresh(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Sheet open={!!tid} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="tariff-drawer">
        <SheetHeader><SheetTitle className="flex items-center gap-2"><Layers className="h-4 w-4" />{t?.name}</SheetTitle></SheetHeader>
        {t && (
          <div className="mt-4 space-y-4">
            <div className="text-sm text-muted-foreground">{t.tariff_type} · {t.external_id || "ohne ID"}</div>
            {write && <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setAddVer(true)} data-testid="add-tariff-version"><GitBranch className="h-4 w-4" />Neue Version</Button>}
            <div className="space-y-2">
              {versions.map((v) => (
                <div key={v.id} className="rounded-lg border border-border p-3" data-testid={`tariff-version-${v.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-medium">v{v.version_no} · {fmtEUR(v.monthly_fee)}/Mon.</div>
                    <StatusBadge status={v.status} map={MF_VERSION_STATUS_MAP} />
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    gültig ab {fmtDate(v.valid_from)}{v.valid_to ? `–${fmtDate(v.valid_to)}` : ""} · {v.data_volume || "—"} · {v.min_term_months} Mon.
                    {v.approved_by && <> · freigegeben von {v.approved_by}</>}
                  </div>
                  <div className="mt-2 flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                    {write && v.status === "entwurf" && <Button size="sm" variant="outline" className="gap-1" onClick={() => action(v.id, "submit")} data-testid={`tv-submit-${v.id}`}><Send className="h-3.5 w-3.5" />Einreichen</Button>}
                    {canApprove && v.status === "zur_freigabe" && <>
                      <Button size="sm" variant="outline" className="gap-1 text-emerald-600" onClick={() => action(v.id, "approve")} data-testid={`tv-approve-${v.id}`}><Check className="h-3.5 w-3.5" />Freigeben</Button>
                      <Button size="sm" variant="outline" className="gap-1 text-red-600" onClick={() => action(v.id, "reject")} data-testid={`tv-reject-${v.id}`}><X className="h-3.5 w-3.5" />Ablehnen</Button>
                    </>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <AddVersionDialog open={addVer} tid={tid} onClose={() => setAddVer(false)} onDone={() => { setAddVer(false); refresh(); }} />
      </SheetContent>
    </Sheet>
  );
}

function AddVersionDialog({ open, tid, onClose, onDone }) {
  const [v, setV] = useState(VEMPTY);
  const submit = async (e) => {
    e.preventDefault();
    if (!v.valid_from) return toast.error("Gültig-ab-Datum erforderlich");
    try {
      await api.post(`/mobilfunk/tariffs/${tid}/versions`, { ...v, monthly_fee: Number(v.monthly_fee) || 0, onetime_fee: Number(v.onetime_fee) || 0, setup_fee: Number(v.setup_fee) || 0, min_term_months: Number(v.min_term_months) || 0 });
      toast.success("Version angelegt (Entwurf)"); setV(VEMPTY); onDone();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="add-version-dialog">
        <DialogHeader><DialogTitle>Neue Tarifversion</DialogTitle>
          <DialogDescription>Freigegebene Versionen werden nicht überschrieben.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Gültig ab</Label><Input type="date" value={v.valid_from} onChange={(e) => setV((s) => ({ ...s, valid_from: e.target.value }))} data-testid="av-validfrom" /></div>
            <div className="space-y-1.5"><Label>Grundgebühr €</Label><Input type="number" step="0.01" value={v.monthly_fee} onChange={(e) => setV((s) => ({ ...s, monthly_fee: e.target.value }))} data-testid="av-monthlyfee" /></div>
            <div className="space-y-1.5"><Label>Datenvolumen</Label><Input value={v.data_volume} onChange={(e) => setV((s) => ({ ...s, data_volume: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Laufzeit (Monate)</Label><Input type="number" value={v.min_term_months} onChange={(e) => setV((s) => ({ ...s, min_term_months: e.target.value }))} /></div>
          </div>
          <div className="space-y-1.5"><Label>Änderungszusammenfassung</Label><Input value={v.change_summary} onChange={(e) => setV((s) => ({ ...s, change_summary: e.target.value }))} /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="av-save">Anlegen</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
